import React from "react";
import { DraggableSidebar, IDraggableSidebarItem } from "../../controls";
import { useARSContext } from "../contexts";
import { useArsTable } from "./ArsTableContextProvider";
import { ArsTableSidebarFilter } from "./ArsTableSidebarFilter";
import { ArsTableSidebarViewRadio } from "./ArsTableSidebarViewRadio";
import { GroupingFactorSidebarItem } from "./GroupingFactorSidebarItem";
import {
  ArsTableSidebarItemAnalysisMethod,
  ArsTableSidebarItemGroupingFactor,
  ArsTableSidebarItemKind,
  ArsTableSidebarItemOperation,
  type ArsTableSidebarItemData,
} from "./interface";
import { OperationSidebarItem } from "./OperationSidebarItem";

/**
 * Renders the ArsTable draggable sidebar for both grouping factors and operations views.
 * The sidebar header contains a radio button to swap between views and a text filter.
 * When viewing operations, the selected analysis method must be set.
 *
 * @returns Sidebar container with view radio, filter input, and draggable items
 */
export const ArsTableDraggableSidebar = (): React.ReactNode => {
  const { state } = useArsTable();
  const { state: arsState } = useARSContext();

  const currentView = state?.settings.currentSidebar.view ?? "groupingFactor";
  const filterText = (state?.settings.currentSidebar.filter ?? "").toLowerCase();
  const analysisMethodId = state?.bodyData.analysisMethodId ?? "";

  const groupSidebarItems: IDraggableSidebarItem<ArsTableSidebarItemGroupingFactor>[] =
    Object.values(arsState.groupingFactors).map((gf) => ({
      id: `ars-table-sidebar-grouping-factor-${gf.id}`,
      label: gf.description && gf.description.trim() !== "" ? gf.description.trim() : gf.name,
      data: {
        kind: ArsTableSidebarItemKind.GroupingFactor,
        dropItem: gf,
      },
    }));
  const analysisMethod = arsState.analysisMethods[analysisMethodId];
  const analysisItems: IDraggableSidebarItem<ArsTableSidebarItemAnalysisMethod>[] = analysisMethod
    ? [
        {
          id: `ars-table-sidebar-analysis-method-${analysisMethod.id}`,
          label:
            analysisMethod.description && analysisMethod.description.trim() !== ""
              ? analysisMethod.description.trim()
              : analysisMethod.name,
          data: {
            kind: ArsTableSidebarItemKind.AnalysisMethod,
            dropItem: analysisMethod,
          },
        },
      ]
    : [];
  const operationSidebarItems: IDraggableSidebarItem<ArsTableSidebarItemOperation>[] = (
    analysisMethod?.operations ?? []
  ).map((operation) => {
    return {
      id: `ars-table-sidebar-operation-${operation.id}`,
      label: operation.label ?? operation.name,
      data: {
        kind: ArsTableSidebarItemKind.Operation,
        dropItem: operation,
      },
    };
  });

  const sidebarItems: IDraggableSidebarItem<ArsTableSidebarItemData>[] = [
    ...(currentView === "groupingFactor"
      ? groupSidebarItems
      : [...analysisItems, ...operationSidebarItems]),
  ].filter((item) => item.label.toLowerCase().includes(filterText));

  /**
   * Callback to render each sidebar item.
   *
   * @param item - Sidebar item to render
   * @param selected - Whether the item is selected
   */
  const renderItem = (
    item: IDraggableSidebarItem<ArsTableSidebarItemData>,
    selected: boolean,
  ): React.ReactElement => (
    <>
      {item.data.kind === ArsTableSidebarItemKind.GroupingFactor ? (
        <GroupingFactorSidebarItem
          item={item}
          selected={selected}
        />
      ) : (
        <OperationSidebarItem
          item={item}
          selected={selected}
        />
      )}
    </>
  );

  return !state ? null : (
    <div className="d-flex justify-content-end align-items-start">
      <DraggableSidebar<ArsTableSidebarItemData>
        show={state.settings.showSidebar}
        side="right"
        width={state.settings.sidebarWidth}
        bodyLevel={true}
        pushContent={true}
        items={sidebarItems}
        header={
          <div className="d-flex flex-column gap-2">
            <ArsTableSidebarViewRadio />
            <ArsTableSidebarFilter />
          </div>
        }
        multiSelect={true}
        renderItem={renderItem}
      />
    </div>
  );
};

ArsTableDraggableSidebar.displayName = "ArsTableDraggableSidebar";
